import React, {
  useState,
  useRef,
  useEffect,
  Fragment,
  useCallback,
} from "react";
import toastr from "toastr";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { postData } from "../../services/api";
import { allowedImageExtensions } from "../../helpers";
import ButtonFile from "../form/ButtonFile";
import Image from "../elements/Image";
import Input from "../form/Input";
import Checkbox from "../form/Checkbox";
import Button from "../form/Button";
import { setTitle } from "../../helpers/MetaTag";
import NoDataFound from "../NoDataFound";
import Searching from "../Searching";
import AlertPopup from "./AlertPopup";
import Select from "../form/Select";
import { useDebounce } from "../../helpers/useDebounce";
import TierSelectDropdown from "../elements/TierSelectDropdown";
import { PositionTier } from "../../constant/Constant";
import { useSettingsModal } from "../../Context/SettingsContext";

const ManagePositionPopup = ({
  isOpen,
  setIsOpen = () => {},
  data,
  loadPositionList = () => {},
  id,
  setIsCloseModal = () => {},
}) => {
  const { i18n, t } = useTranslation();
  const [details, setDetails] = useState({});
  const [name, setName] = useState("");
  const [shortName, setShortName] = useState();
  const [isAdd, setIsAdd] = useState(false);
  const [isCancel, setIsCancel] = useState(false);
  const role = localStorage.getItem("role");
  const [enabledAlertPopup, setEnabledAlertPopup] = useState(false);
  const [actionValue, setActionValue] = useState({});
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTier, setSelectedTier] = useState("");
  const search = useDebounce(name, 500);
  const { tierType } = useSettingsModal();

  useEffect(() => {
    // Position List
    const loadPosition = async () => {
      setLoading(true);
      try {
        const result = await postData("company-position/list", {
          keyword: search,
          limit: 1000000000,
          isGlobal: true,
        });
        if (result.data) {
          setPositions(result.data);
        }
        setLoading(false);
      } catch (err) {
        setLoading(false);
        console.log(err.message);
      }
    };
    loadPosition();
  }, [search]);

  const createAcronym = (phrase) => {
    let string = phrase
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase();
    setShortName(string);
  };

  useEffect(() => {
    // Position details
    const loadPositionDetails = async () => {
      try {
        const result = await postData("position/details", {
          id: id,
        });

        if (result.data) {
          setDetails(result.data);
        }
      } catch (err) {
        console.log(err.message);
      }
    };
    loadPositionDetails();
  }, [id]);

  useEffect(() => {
    setName(details?.name);
    setShortName(details?.short_name);
    setSelectedTier(details?.tierId);
  }, [details, id]);

  const onSubmit = useCallback(async () => {
    if (tierType === PositionTier && !selectedTier) {
      toastr?.error("Please Select a tier");
      return;
    }
    setIsAdd(true);
    try {
      const path = id ? "position/update" : "position/create";
      const payload = {
        ...(id && { id }),
        name,
        short_name: shortName,
        tierId: selectedTier,
      };

      const result = await postData(path, payload);

      if (result.status === 200) {
        toastr.success(result.message);
        setEnabledAlertPopup(false);
        loadPositionList();
        setIsOpen({ open: false, _id: null });
      } else {
        toastr.error(result.message);
        setEnabledAlertPopup(false);
      }
    } catch (error) {
      toastr.error(error.message);
    } finally {
      setIsAdd(false);
      setActionValue({});
    }
  }, [tierType, selectedTier]);

  useEffect(() => {
    if (actionValue && actionValue?.button_type === "confirm") {
      onSubmit();
    }
  }, [actionValue, onSubmit]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-xl transform rounded-2xl bg-white p-4 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900 text-center"
                >
                  {id ? t("edit_position") : t("add_position")}
                </Dialog.Title>
                <div className="absolute top-2 right-2 z-10">
                  <Button
                    buttonLabelClasses=""
                    buttonType={"button"}
                    buttonIcon={"fa-light fa-times"}
                    buttonIconPosition={"left"}
                    buttonLabel={""}
                    buttonClasses={
                      "!bg-transparent !text-slate-700 !w-10 !h-10 !p-0"
                    }
                    functions={() => {
                      setIsOpen({ _id: null, open: false });
                      setDetails("");
                      setName("");
                      setShortName("");
                      setIsCloseModal(true);
                    }}
                    buttonHasLink={false}
                  />
                </div>
                <div className="relative mt-4">
                  <div className="grid grid-cols-12 gap-4">
                    <div className="col-span-12 flex flex-col">
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 gap-4">
                          <div className="relative w-full group z-10">
                            <Input
                              label={t("position")}
                              requiredDisplay={true}
                              labelClasses={"!text-xs"}
                              inputType={"text"}
                              inputPlaceholder={t("enter_position")}
                              inputValue={name}
                              setInput={(val) => {
                                setName(val);
                                createAcronym(val);
                              }}
                            />
                            {positions &&
                              Array?.isArray(positions) &&
                              positions?.length > 0 && (
                                <div className="hidden group-focus-within:block absolute top-full left-0 w-full mt-1">
                                  <div className="relative bg-white border border-slate-100 shadow-md py-1 overflow-hidden rounded-lg">
                                    <div className="overflow-auto scrollbar max-h-60">
                                      {positions &&
                                        Array?.isArray(positions) &&
                                        positions?.length > 0 &&
                                        positions?.map((item) => (
                                          <>
                                            <Button
                                              buttonLabel={
                                                item?.name +
                                                " - " +
                                                item?.short_name
                                              }
                                              buttonLabelClasses={"!text-xs"}
                                              buttonClasses={
                                                "w-full !h-8 !justify-start !bg-transparent hover:!bg-slate-100 !text-slate-500 hover:!text-600"
                                              }
                                              functions={(e) => {
                                                setName(item?.name);
                                                createAcronym(item?.name);
                                                setShortName(item?.short_name);
                                              }}
                                            />
                                          </>
                                        ))}
                                    </div>
                                  </div>
                                </div>
                              )}
                          </div>
                          <div className="relative w-full">
                            <Input
                              label={t("position_abbreviation")}
                              labelClasses={"!text-xs"}
                              inputType={"text"}
                              inputPlaceholder={t("enter_position_abreviation")}
                              inputValue={shortName}
                              setInput={setShortName}
                            />
                          </div>
                          <div className="relative w-full">
                            <TierSelectDropdown
                              className={"!w-full"}
                              selectedTier={selectedTier}
                              setSelectedTier={setSelectedTier}
                              isLable={true}
                              isDefaultTier={false}
                              isSelect={true}
                              requiredDisplay={true}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-center gap-4 mt-5">
                        <div className="flex items-center justify-center gap-4">
                          <Button
                            buttonClasses={
                              "bg-white border border-slate-200 !text-slate-400 ml-2"
                            }
                            buttonLabelClasses=""
                            buttonType={"button"}
                            buttonIcon={"fa-regular fa-xmark"}
                            buttonIconPosition={"left"}
                            buttonLabel={t("cancel")}
                            functions={() => {
                              setIsOpen({ _id: null, open: false });
                              setDetails("");
                              setName("");
                              setShortName("");
                              setIsCloseModal(true);
                              setActionValue({});
                            }}
                            buttonHasLink={false}
                          />
                          <Button
                            buttonClasses=""
                            buttonLabelClasses=""
                            buttonType={"button"}
                            buttonIcon={
                              isAdd
                                ? "fa-light fa-spinner fa-spin"
                                : "fa-light fa-check"
                            }
                            buttonIconPosition={"left"}
                            buttonLabel={t("save")}
                            //functions={onSubmit}
                            functions={() => {
                              if (!name) {
                                toastr.error(t("position_field_is_required"));
                              } else {
                                setEnabledAlertPopup(true);
                              }
                            }}
                            buttonHasLink={false}
                            buttonDisabled={isAdd}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  {enabledAlertPopup && (
                    <AlertPopup
                      isOpen={enabledAlertPopup}
                      setIsOpen={(val) => {
                        setEnabledAlertPopup(val);
                        setActionValue({});
                      }}
                      getActionValue={(obj) => {
                        setActionValue(obj);
                      }}
                    />
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ManagePositionPopup;
